/**
 * Motor de la calculadora Aguacate COP (RAIL COLOMBIA) — modelo del xls
 * `carlos estimacion COP` alimentado con TASAS REALES en vivo.
 *
 * 100% cálculo puro (sin dinero real). Fiel al xls, con dos diferencias que
 * lo hacen real en vez de teórico:
 *
 *   · B3 (trmVita / "tasa de referencia") ← SET-ICAP (mercado, `trmcop`).
 *   · B4 (trmSetup / rate al que Aguacate COMPRA los COP) ← usd_sell Vita
 *     COBREB (`vita-wallet/prices`).  Antes el xls lo asumía = trmVita×0.995.
 *
 * De ahí:
 *   · B5 (spread real) = 1 − trmSetup/trmVita = 1 − usd_sell/SET-ICAP
 *     (Hallazgo #3: el 0.5% asumido por la hoja, ahora con dato vivo).
 *   · Markup C por monto (col C3..C13) = 100×(1 − (D−K)/(D×(1−B5))),
 *     deriva de la comisión K = aguacateFxPct×D + aguacateFijo. VARÍA por
 *     monto (alto en montos chicos por el fijo, ~1.2% asintótico).
 *
 * Identidad verificada del xls: copAguacate = trmVita × (D − K)
 * (independiente de trmSetup). El trmSetup sólo mueve el markup C mostrado y
 * la ganancia (costo al que Aguacate consigue los COP).
 *
 * Si no se pasa `trmSetup`, cae al supuesto clásico trmVita×0.995 (reproduce
 * la hoja original exacto).
 */

export interface CalcConfig {
  /** B3 — tasa de referencia / mercado (SET-ICAP). */
  trmVita: number;
  /** B4 — rate real de compra (usd_sell Vita COBREB). Opcional. */
  trmSetup?: number;
  /** B16 — comisión % visible de Vita (referencia). */
  vitaFxPct: number;
  /** B17 — comisión fija USD visible de Vita (referencia). */
  vitaFijo: number;
  /** B20 — comisión % visible de Aguacate (editable). */
  aguacateFxPct: number;
  /** B21 — comisión fija USD visible de Aguacate (editable). */
  aguacateFijo: number;
  /** E3:E13 — costo operativo fijo interno en COP por operación. */
  costoFijoCOP: number;
}

export interface CalcResultado {
  montoUSD: number;
  comisionVita: number; // J
  comisionAguacate: number; // K
  diferenciaPctComision: number; // L
  trmVita: number; // B3
  trmSetup: number; // B4
  pctSetup: number; // B5 (spread real)
  markupPct: number; // C ★ (varía por monto)
  copVita: number; // F
  copAguacate: number; // G ★
  costoVitaUSD: number; // H
  gananciaAguacateUSD: number; // I ★
  ventajaVsVitaCOP: number;
  ventajaVsVitaPct: number;
}

export const CALC_CONFIG_DEFAULT: CalcConfig = {
  trmVita: 3624.6,
  trmSetup: undefined,
  vitaFxPct: 0.017,
  vitaFijo: 0.8148,
  aguacateFxPct: 0.0169,
  aguacateFijo: 0.81,
  costoFijoCOP: 1500,
};

export const MONTOS_TABLA = [10, 20, 50, 100, 200, 500, 1000, 2000, 5000];

/** B4 y B5 — rate de compra y spread real. trmSetup explícito o ×0.995. */
function calcSetup(cfg: CalcConfig): { trmSetup: number; pctSetup: number } {
  const trmSetup =
    cfg.trmSetup && cfg.trmSetup > 0 ? cfg.trmSetup : cfg.trmVita * 0.995;
  const pctSetup = cfg.trmVita > 0 ? 1 - trmSetup / cfg.trmVita : 0;
  return { trmSetup, pctSetup };
}

const ZERO_RESULTADO = (cfg: CalcConfig): CalcResultado => {
  const { trmSetup, pctSetup } = calcSetup(cfg);
  return {
    montoUSD: 0,
    comisionVita: 0,
    comisionAguacate: 0,
    diferenciaPctComision: 0,
    trmVita: cfg.trmVita,
    trmSetup,
    pctSetup,
    markupPct: 0,
    copVita: 0,
    copAguacate: 0,
    costoVitaUSD: 0,
    gananciaAguacateUSD: 0,
    ventajaVsVitaCOP: 0,
    ventajaVsVitaPct: 0,
  };
};

/** Resultado completo de una operación (todas las columnas de una fila). */
export function calcOperacion(
  montoUSD: number,
  cfg: CalcConfig = CALC_CONFIG_DEFAULT,
): CalcResultado {
  if (!(montoUSD > 0) || !(cfg.trmVita > 0)) return ZERO_RESULTADO(cfg);

  const { trmSetup, pctSetup } = calcSetup(cfg);

  const comisionVita = cfg.vitaFxPct * montoUSD + cfg.vitaFijo; // J
  const comisionAguacate = cfg.aguacateFxPct * montoUSD + cfg.aguacateFijo; // K
  const diferenciaPct =
    comisionAguacate !== 0 ? 1 - comisionVita / comisionAguacate : 0; // L

  // C = 100 × (1 − (D − K) / (D × (1 − B5)))  ← markup por monto
  const markupPct =
    100 * (1 - (montoUSD - comisionAguacate) / (montoUSD * (1 - pctSetup)));
  const copVita = cfg.trmVita * (montoUSD - comisionVita); // F
  const copAguacate = trmSetup * ((100 - markupPct) / 100) * montoUSD; // G
  const costoVitaUSD = (copAguacate + cfg.costoFijoCOP) / trmSetup; // H
  const gananciaAguacateUSD = montoUSD - costoVitaUSD; // I

  const ventajaVsVitaCOP = copAguacate - copVita;
  const ventajaVsVitaPct =
    copVita > 0 ? (ventajaVsVitaCOP / copVita) * 100 : 0;

  return {
    montoUSD,
    comisionVita,
    comisionAguacate,
    diferenciaPctComision: diferenciaPct,
    trmVita: cfg.trmVita,
    trmSetup,
    pctSetup,
    markupPct,
    copVita,
    copAguacate,
    costoVitaUSD,
    gananciaAguacateUSD,
    ventajaVsVitaCOP,
    ventajaVsVitaPct,
  };
}

/** Tabla por montos estándar (filas 3–13). Cada fila trae su markup C propio. */
export function calcTabla(
  cfg: CalcConfig = CALC_CONFIG_DEFAULT,
): CalcResultado[] {
  return MONTOS_TABLA.map((m) => calcOperacion(m, cfg));
}
